import LegalLayout from '../components/LegalLayout';

export default function PrivacyPage() {
    return (
        <LegalLayout title="Privacy Policy" lastUpdated="November 23, 2025">
            <p className="text-gray-300 text-lg mb-8">
                DareUp ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how
                we collect, use, disclose, and safeguard your information when you use our mobile application.
            </p>

            <Section title="1. Information We Collect">
                <Subsection title="1.1 Information You Provide:">
                    <ul>
                        <li>
                            <strong>Account Information:</strong> Email address, username, display name, birthdate,
                            password (encrypted)
                        </li>
                        <li>
                            <strong>Profile Information:</strong> Avatar/profile photo, bio
                        </li>
                        <li>
                            <strong>Content:</strong> Videos, captions, comments, likes, and other content you post
                        </li>
                        <li>
                            <strong>Communications:</strong> Messages you send through the App
                        </li>
                    </ul>
                </Subsection>

                <Subsection title="1.2 Automatically Collected Information:">
                    <ul>
                        <li>
                            <strong>Device Information:</strong> Device type, operating system, unique device
                            identifiers
                        </li>
                        <li>
                            <strong>Usage Data:</strong> Features used, time spent, videos watched, interactions
                        </li>
                        <li>
                            <strong>Location Data:</strong> Approximate location based on IP address (precise location
                            only with permission)
                        </li>
                        <li>
                            <strong>Log Data:</strong> IP address, browser type, access times, pages viewed
                        </li>
                    </ul>
                </Subsection>

                <Subsection title="1.3 Information from Third Parties:">
                    <ul>
                        <li>
                            <strong>Social Media:</strong> If you sign in with Google or Apple, we receive basic profile
                            information
                        </li>
                        <li>
                            <strong>Analytics Providers:</strong> Aggregated usage statistics
                        </li>
                    </ul>
                </Subsection>
            </Section>

            <Section title="2. How We Use Your Information">
                <p>We use your information to:</p>
                <ul>
                    <li>Provide, maintain, and improve the App</li>
                    <li>Create and manage your account</li>
                    <li>Personalize your experience and content recommendations</li>
                    <li>Enable social features (following, comments, likes)</li>
                    <li>Send notifications and updates</li>
                    <li>Respond to your requests and support inquiries</li>
                    <li>Detect and prevent fraud, abuse, and security threats</li>
                    <li>Comply with legal obligations</li>
                    <li>Analyze usage patterns and improve our services</li>
                    <li>Send marketing communications (with your consent)</li>
                </ul>
            </Section>

            <Section title="3. How We Share Your Information">
                <p>We may share your information with:</p>

                <Subsection title="3.1 Public Information:">
                    <ul>
                        <li>Your profile information (username, display name, avatar, bio) is publicly visible</li>
                        <li>Videos you post publicly are visible to all users</li>
                        <li>Your follower/following lists are visible to other users</li>
                    </ul>
                </Subsection>

                <Subsection title="3.2 Service Providers:">
                    <ul>
                        <li>Cloud hosting providers (AWS)</li>
                        <li>Analytics services</li>
                        <li>Customer support tools</li>
                        <li>Payment processors (for future features)</li>
                    </ul>
                </Subsection>

                <Subsection title="3.3 Legal Requirements:">
                    <ul>
                        <li>To comply with laws, regulations, or legal processes</li>
                        <li>To protect our rights, property, or safety</li>
                        <li>In connection with legal claims or investigations</li>
                    </ul>
                </Subsection>

                <Subsection title="3.4 Business Transfers:">
                    <p>In connection with mergers, acquisitions, or sale of assets</p>
                </Subsection>

                <Subsection title="3.5 With Your Consent:">
                    <p>When you explicitly agree to share information</p>
                </Subsection>

                <p className="font-semibold text-white mt-4">
                    We do NOT sell your personal information to third parties.
                </p>
            </Section>

            <Section title="4. Data Security">
                <p>We implement industry-standard security measures to protect your information:</p>
                <ul>
                    <li>Encryption in transit (HTTPS/TLS)</li>
                    <li>Encrypted storage of sensitive data</li>
                    <li>Access controls and authentication</li>
                    <li>Regular security audits</li>
                </ul>
                <p>
                    However, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute
                    security.
                </p>
            </Section>

            <Section title="5. Your Rights and Choices">
                <Subsection title="5.1 Access and Update:">
                    <ul>
                        <li>View and update your profile information in App settings</li>
                        <li>Request a copy of your data</li>
                    </ul>
                </Subsection>

                <Subsection title="5.2 Delete Your Account:">
                    <ul>
                        <li>Delete your account through App settings</li>
                        <li>Your content will be permanently removed</li>
                    </ul>
                </Subsection>

                <Subsection title="5.3 Privacy Settings:">
                    <ul>
                        <li>Control who can see your content</li>
                        <li>Manage notification preferences</li>
                        <li>Control data collection settings</li>
                    </ul>
                </Subsection>

                <Subsection title="5.4 Marketing Communications:">
                    <ul>
                        <li>Opt out of marketing emails via unsubscribe links</li>
                        <li>Disable push notifications in App settings</li>
                    </ul>
                </Subsection>

                <Subsection title="5.5 Data Portability:">
                    <p>Request a copy of your data in a portable format</p>
                </Subsection>

                <Subsection title="5.6 Rights Under GDPR (EU Users):">
                    <ul>
                        <li>Right to access, rectification, erasure</li>
                        <li>Right to restrict processing</li>
                        <li>Right to data portability</li>
                        <li>Right to object to processing</li>
                        <li>Right to withdraw consent</li>
                    </ul>
                </Subsection>

                <Subsection title="5.7 Rights Under CCPA (California Users):">
                    <ul>
                        <li>Right to know what personal information is collected</li>
                        <li>Right to delete personal information</li>
                        <li>Right to opt-out of sale (we don't sell data)</li>
                        <li>Right to non-discrimination</li>
                    </ul>
                </Subsection>
            </Section>

            <Section title="6. Data Retention">
                <p>
                    We retain your information for as long as your account is active or as needed to provide services.
                    When you delete your account:
                </p>
                <ul>
                    <li>Personal information is deleted within 30 days</li>
                    <li>Some data may be retained for legal compliance or backup purposes</li>
                    <li>Public content you posted may remain in other users' caches</li>
                </ul>
            </Section>

            <Section title="7. Children's Privacy">
                <p>
                    DareUp is intended for users aged 13 and above. We comply with the Children's Online Privacy
                    Protection Act (COPPA).
                </p>
                <ul>
                    <li>We do not knowingly collect information from children under 13</li>
                    <li>If we discover information from a child under 13, we will delete it immediately</li>
                    <li>Parents/guardians can contact us to request deletion of their child's data</li>
                </ul>
                <p className="mt-4">For users aged 13-17:</p>
                <ul>
                    <li>We encourage parental involvement</li>
                    <li>Certain features may be restricted</li>
                    <li>Privacy settings default to more restrictive options</li>
                </ul>
            </Section>

            <Section title="8. International Data Transfers">
                <p>
                    Your information may be transferred to and processed in countries other than your own. We ensure
                    appropriate safeguards are in place:
                </p>
                <ul>
                    <li>Standard Contractual Clauses (EU)</li>
                    <li>Privacy Shield framework (where applicable)</li>
                    <li>Other approved transfer mechanisms</li>
                </ul>
            </Section>

            <Section title="9. Cookies and Tracking Technologies">
                <p>We use cookies and similar technologies to:</p>
                <ul>
                    <li>Remember your preferences</li>
                    <li>Analyze usage patterns</li>
                    <li>Improve App performance</li>
                    <li>Deliver personalized content</li>
                </ul>
                <p>
                    You can control cookies through your device settings, but some features may not function properly if
                    disabled.
                </p>
            </Section>

            <Section title="10. Third-Party Links">
                <p>
                    The App may contain links to third-party websites or services. We are not responsible for their
                    privacy practices. Please review their privacy policies.
                </p>
            </Section>

            <Section title="11. Do Not Track">
                <p>
                    We currently do not respond to Do Not Track signals, as there is no industry standard for
                    compliance.
                </p>
            </Section>

            <Section title="12. Changes to This Privacy Policy">
                <p>
                    We may update this Privacy Policy from time to time. We will notify you of material changes through:
                </p>
                <ul>
                    <li>In-app notifications</li>
                    <li>Email notifications</li>
                    <li>Updated "Last Updated" date</li>
                </ul>
                <p>Continued use after changes constitutes acceptance of the updated Privacy Policy.</p>
            </Section>

            <Section title="13. Your California Privacy Rights">
                <p>California residents have additional rights under the California Consumer Privacy Act (CCPA):</p>
                <ul>
                    <li>Right to know what personal information is collected, used, shared, or sold</li>
                    <li>Right to delete personal information</li>
                    <li>Right to opt-out of sale (we don't sell data)</li>
                    <li>Right to non-discrimination for exercising privacy rights</li>
                </ul>
                <p>
                    To exercise these rights, contact us at{' '}
                    <a href="mailto:privacy@dareup.com" className="text-primary-400 hover:text-primary-300">
                        privacy@dareup.com
                    </a>
                    .
                </p>
            </Section>

            <Section title="14. European Privacy Rights">
                <p>EU residents have rights under the General Data Protection Regulation (GDPR):</p>
                <ul>
                    <li>Legal basis for processing: Consent, contract performance, legitimate interests</li>
                    <li>Data controller: Amboyance Inc.</li>
                    <li>
                        Data protection officer:{' '}
                        <a href="mailto:privacy@dareup.com" className="text-primary-400 hover:text-primary-300">
                            privacy@dareup.com
                        </a>
                    </li>
                    <li>Right to lodge complaints with supervisory authorities</li>
                </ul>
            </Section>

            <Section title="15. Contact Us">
                <p>
                    If you have questions or concerns about this Privacy Policy or our data practices, please contact
                    us:
                </p>
                <div className="bg-dark-800 rounded-xl p-6 mt-4">
                    <p className="font-semibold text-white mb-2">Amboyance Inc.</p>
                    <p>
                        Email:{' '}
                        <a href="mailto:privacy@dareup.com" className="text-primary-400 hover:text-primary-300">
                            privacy@dareup.com
                        </a>
                    </p>
                    <p>
                        Data Protection Officer:{' '}
                        <a href="mailto:dpo@dareup.com" className="text-primary-400 hover:text-primary-300">
                            dpo@dareup.com
                        </a>
                    </p>
                    <p>
                        Support:{' '}
                        <a href="mailto:support@dareup.com" className="text-primary-400 hover:text-primary-300">
                            support@dareup.com
                        </a>
                    </p>
                    <p>Address: 2802-99 John St, Toronto, Ontario, M5V 0S6, Canada</p>
                    <p className="mt-2 text-gray-400">
                        Response Time: We aim to respond to privacy requests within 30 days.
                    </p>
                </div>
            </Section>

            <Section title="16. Consent">
                <p>
                    By using DareUp, you consent to the collection, use, and sharing of your information as described in
                    this Privacy Policy.
                </p>
            </Section>
        </LegalLayout>
    );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <section className="mb-10">
            <h2 className="font-display text-xl sm:text-2xl font-bold text-white mb-4">{title}</h2>
            <div className="text-gray-300 space-y-4 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:space-y-2 [&_li]:text-gray-300">
                {children}
            </div>
        </section>
    );
}

function Subsection({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div className="mb-4">
            <h3 className="font-semibold text-white mb-2">{title}</h3>
            <div className="[&>ul]:list-disc [&>ul]:pl-6 [&>ul]:space-y-1">{children}</div>
        </div>
    );
}
